import React from 'react';
import styled from 'styled-components';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { Mail, Check, ArrowRight } from 'react-feather';
import Container from '../../VuexyDesign/atoms/Container';
import { useIsDesktop } from '../../style/constants';
import BrowserPageTitle from '../common/BrowserPageTitle';
import FriendlyQuestions from './FriendlyQuestions';
import { style } from '../../api';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const BecomePro = (_props: BecomeProProps) => {
  const { t } = useTranslation('pricing');
  const isDesktop = useIsDesktop();

  const features = [
    'Unlimited global distribution to 150+ stores',
    'Up to 100 artists or bands',
    'Custom label name',
    'Custom release date',
    'Customizable iTunes pricing',
    'Priority support',
    'Keep up to 100% of your revenue',
    'Real-time analytics & reporting',
  ];

  return (
    <>
      <BrowserPageTitle>{t('pricing:pageTitle') as string}</BrowserPageTitle>
      <PageWrapper>
        <Container className={`mx-auto ${isDesktop ? 'w-75' : 'w-100 px-1'}`}>
          {/* Hero Section */}
          <HeroSection className="text-center">
            <Eyebrow>DISTRIBUTION</Eyebrow>
            <HeroTitle>
              Music Distribution
              <br />
              <GradientText>Built for Professionals</GradientText>
            </HeroTitle>
            <HeroSubtitle>
              Get your music on every major platform worldwide. We work
              exclusively with serious artists and labels.
            </HeroSubtitle>
          </HeroSection>

          {/* Single Exclusive Card */}
          <CardWrapper className={isDesktop ? '' : 'px-0'}>
            <ExclusiveCard>
              <CardGlow />
              <CardInner>
                <div
                  className={`d-flex ${
                    isDesktop ? 'flex-row align-items-stretch' : 'flex-column'
                  }`}
                  style={{ gap: isDesktop ? '3rem' : '2rem' }}
                >
                  {/* Left: Info */}
                  <div
                    className="d-flex flex-column justify-content-center"
                    style={{ flex: isDesktop ? '1' : 'auto' }}
                  >
                    <PlanBadge>INQUIRY ONLY</PlanBadge>
                    <CardTitle>VIRPP Pro</CardTitle>
                    <CardSubtitle>
                      Tailored plans for artists & labels. Contact our team to
                      discuss a plan that fits your needs.
                    </CardSubtitle>

                    <CTAButton href="mailto:hello@virpp.com">
                      <Mail size={18} />
                      <span>Contact Sales</span>
                      <ArrowRight size={16} />
                    </CTAButton>
                    <CTAHint>hello@virpp.com</CTAHint>
                  </div>

                  {/* Right: Features */}
                  <div
                    className="d-flex flex-column justify-content-center"
                    style={{ flex: isDesktop ? '1' : 'auto' }}
                  >
                    <FeaturesTitle>What&apos;s included</FeaturesTitle>
                    <FeaturesList>
                      {features.map((feature) => (
                        <FeatureItem key={feature}>
                          <CheckIcon>
                            <Check size={16} />
                          </CheckIcon>
                          <span>{feature}</span>
                        </FeatureItem>
                      ))}
                    </FeaturesList>
                  </div>
                </div>
              </CardInner>
            </ExclusiveCard>
          </CardWrapper>

          {/* Trusted By Section */}
          <TrustedSection>
            <TrustedTitle>Distribute to 150+ stores including</TrustedTitle>
            <TrustedLogos>
              <Image
                src="/static/images/Spotify.svg"
                alt="Spotify"
                width={100}
                height={30}
                style={{ objectFit: 'contain', opacity: 0.5 }}
              />
              <Image
                src="/static/images/AppleMusic.png"
                alt="Apple Music"
                width={100}
                height={30}
                style={{ objectFit: 'contain', opacity: 0.5 }}
              />
              <Image
                src="/static/images/amazon.png"
                alt="Amazon Music"
                width={100}
                height={30}
                style={{ objectFit: 'contain', opacity: 0.5 }}
              />
              <Image
                src="/static/images/tidal.png"
                alt="Tidal"
                width={100}
                height={30}
                style={{ objectFit: 'contain', opacity: 0.5 }}
              />
              <Image
                src="/static/images/youtube-music.png"
                alt="YouTube Music"
                width={100}
                height={30}
                style={{ objectFit: 'contain', opacity: 0.5 }}
              />
            </TrustedLogos>
          </TrustedSection>

          {/* FAQ */}
          <FriendlyQuestions />
        </Container>
      </PageWrapper>
    </>
  );
};

/* ─── Styled Components ─── */

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 2rem 0 4rem;
`;

const HeroSection = styled.div`
  padding: 4rem 0 3rem;
`;

const Eyebrow = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3px;
  color: ${style.primary_pink_new};
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 1rem 0;
  color: #1a1a2e;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #f41657, #1159f1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1.15rem;
  color: #6b7280;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
`;

const CardWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0 3rem;
`;

const ExclusiveCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  border-radius: 20px;
  overflow: hidden;
`;

const CardGlow = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #f41657 0%, #1159f1 100%);
  border-radius: 20px;
  padding: 2px;
  z-index: 0;
`;

const CardInner = styled.div`
  position: relative;
  z-index: 1;
  background: #ffffff;
  border-radius: 18px;
  margin: 2px;
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const PlanBadge = styled.span`
  display: inline-block;
  width: fit-content;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #fff;
  background: linear-gradient(135deg, #f41657, #1159f1);
  padding: 6px 16px;
  border-radius: 20px;
  margin-bottom: 1.25rem;
`;

const CardTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 0.75rem;
`;

const CardSubtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 1.5rem;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 14px 32px;
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
  color: #fff;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(26, 26, 46, 0.3);
    color: #fff;
    text-decoration: none;
  }
`;

const CTAHint = styled.span`
  display: block;
  margin-top: 0.75rem;
  font-size: 13px;
  color: #9ca3af;
`;

const FeaturesTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #9ca3af;
  margin: 0 0 1rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #374151;
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ecfdf5;
  color: #10b981;
  flex-shrink: 0;
`;

const TrustedSection = styled.div`
  text-align: center;
  margin: 2rem 0 3rem;
`;

const TrustedTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #9ca3af;
  margin-bottom: 1.5rem;
`;

const TrustedLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  flex-wrap: wrap;
`;

interface BecomeProProps {
  pricingNav?: boolean;
  pricingHeader?: boolean;
  pricingCards?: boolean;
  paymentMethods?: boolean;
  proFeatures?: boolean;
  friendlyQuestions?: boolean;
  pricingBanner?: boolean;
}

export interface Plan {
  title: string;
  newTitle: string;
  subtitle: string;
  includes: string;
  image: string;
  symbolCurrency?: string;
  monthlyPriceCurrency?: string;
  yearlyPriceCurrency?: string;
  monthlyPrice?: string;
  yearlyPrice?: string;
  benefits: Benefit[];
  popular: boolean;
  pro: boolean;
  growth?: boolean;
  amateur?: boolean;
  yearly_plan_id?: string;
  monthly_plan_id?: string;
}

export interface Benefit {
  text: string;
  overline: boolean;
}

export default BecomePro;

export const planBenefits: Plan[] = [
  {
    title: 'Growth',
    newTitle: 'Growing',
    subtitle: 'pricing:payPerRelease',
    includes: 'pricing:pricingCardsBenefits.everythingInStarting',
    image: '/static/images/payment/growth-plan.svg',
    benefits: [
      {
        text: 'pricing:pricingCardsBenefits.distributeOneSong',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.oneArtist',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.revenue',
        overline: false,
      },
    ],
    popular: false,
    pro: true,
    growth: true,
  },
  {
    title: 'Ultra',
    newTitle: 'Performing',
    subtitle: 'pricing:theDashBoardForRecordLabels',
    includes: 'pricing:pricingCardsBenefits.everythingInGrowing',
    image: '/static/images/payment/ultra-plan.svg',
    benefits: [
      {
        text: 'pricing:pricingCardsBenefits.unlimitedDistribution',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.toFiveArtists',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.customLabelName',
        overline: false,
      },

      {
        text: 'pricing:pricingCardsBenefits.customReleaseDate',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.customizableItunes',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.revenue',
        overline: false,
      },
    ],
    popular: true,
    pro: true,
    growth: false,
  },

  {
    title: 'Premium',
    newTitle: 'Ultra',
    subtitle: 'pricing:takeCareerToNextLevel',
    includes: 'pricing:pricingCardsBenefits.everythingInPerforming',
    image: '/static/images/payment/amateur-plan.svg',
    benefits: [
      {
        text: 'pricing:pricingCardsBenefits.unlimitedDistribution',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.toHundredArtists',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.customLabelName',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.customReleaseDate',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.customizableItunes',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.prioritySupport',
        overline: false,
      },
      {
        text: 'pricing:pricingCardsBenefits.revenue',
        overline: false,
      },
    ],
    popular: false,
    pro: true,
    growth: false,
  },
];
